import { useEffect, useRef } from 'react';
import { useDownload } from '../context/DownloadContext';
import { useNavigation } from '../context/NavigationContext';

/**
 * Hook that activates navigation guard when downloads are active
 * Prevents accidental navigation that would cancel downloads
 * 
 * Fixed issues:
 * - Stale closure in onConfirm callback (now uses ref)
 * - Cleanup running on every re-render (split into separate effect)
 * - Excessive re-runs on progress updates (only depend on count)
 * 
 * Usage: Call this hook at the app level (inside components wrapped by both
 * DownloadProvider and NavigationProvider)
 */
const useDownloadNavigationGuard = () => {
  const { 
    hasActiveDownloads, 
    cancelDownload, 
    activeDownloads,
    activeDownloadsRef, // Ref for accessing latest state without stale closure
  } = useDownload();
  const { setNavigationGuard } = useNavigation();
  
  // Track download count to minimize re-renders
  // Only depend on count, not the entire Map (which changes on every progress update)
  const downloadCount = activeDownloads.size;
  const downloadCountRef = useRef(downloadCount);
  downloadCountRef.current = downloadCount;

  // Track if we activated the guard (so we know to deactivate it)
  const guardActivatedByDownloadsRef = useRef(false);

  // Effect 1: Sync guard state with download presence
  useEffect(() => {
    const isActive = hasActiveDownloads();
    
    if (isActive) {
      const count = downloadCountRef.current;
      const downloadText = count === 1 
        ? '1 download' 
        : `${count} downloads`;

      // Activate navigation guard
      guardActivatedByDownloadsRef.current = true;
      setNavigationGuard({
        isActive: true,
        title: 'Cancel Download?',
        message: `You have ${downloadText} in progress. If you leave, ${count === 1 ? 'it' : 'they'} will be cancelled.`,
        confirmText: 'Cancel downloads',
        cancelText: 'Wait for downloads',
        onConfirm: () => {
          // FIX: Use ref to get CURRENT downloads, not stale closure
          // This ensures we cancel the actual downloads at the time of confirmation,
          // not what was in the closure when the guard was set
          const currentDownloads = Array.from(activeDownloadsRef.current.keys());
          currentDownloads.forEach(messageId => cancelDownload(messageId));
        },
      });
    } else if (guardActivatedByDownloadsRef.current) {
      // Only deactivate if WE were the ones who activated it
      // This prevents conflicts with other parts of the app that also use the guard
      guardActivatedByDownloadsRef.current = false;
      setNavigationGuard({ isActive: false });
    }
    // If guard wasn't activated by downloads, don't touch it (let other code manage it)
  }, [
    hasActiveDownloads, 
    downloadCount, // Only re-run when count changes, not on every progress update
    setNavigationGuard, 
    cancelDownload, 
    activeDownloadsRef,
  ]);

  // Effect 2: Cleanup ONLY on unmount (empty deps array)
  // This prevents the guard from being deactivated/reactivated on every state change
  useEffect(() => {
    return () => {
      // Only deactivate on unmount if we activated it
      if (guardActivatedByDownloadsRef.current) {
        setNavigationGuard({ isActive: false });
      }
    };
  }, [setNavigationGuard]);
};

export default useDownloadNavigationGuard;
